import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/Card";
import { functionsService } from "@/services/functionsService";

const DEFAULT_ABOUT_TEXT = `Blazerex is an AI-powered fintech intelligence platform focused on helping investors and teams analyze earnings calls, filings, and market signals faster.

We combine structured analysis with source transcript context so users can ask deeper follow-up questions and make decisions with better clarity.

Visit https://blazerex.web.app for updates.`;

const renderWithLinks = (text: string) => {
  const lines = text.split("\n");
  const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  return lines.map((line, lineIndex) => {
    const segments: React.ReactNode[] = [];
    let cursor = 0;
    let match: RegExpExecArray | null;

    while ((match = markdownLinkRegex.exec(line)) !== null) {
      const [full, label, href] = match;
      const start = match.index;
      if (start > cursor) {
        const before = line.slice(cursor, start);
        segments.push(before);
      }
      segments.push(
        <a
          key={`md-${lineIndex}-${start}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blazerex-green underline hover:opacity-90"
        >
          {label}
        </a>
      );
      cursor = start + full.length;
    }

    const remaining = line.slice(cursor);
    const tailParts = remaining.split(urlRegex);
    tailParts.forEach((part, idx) => {
      if (/^https?:\/\//.test(part)) {
        segments.push(
          <a
            key={`url-${lineIndex}-${idx}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blazerex-green underline hover:opacity-90"
          >
            {part}
          </a>
        );
      } else if (part) {
        segments.push(part);
      }
    });

    return (
      <p key={`line-${lineIndex}`} className="body-text text-gray-700 whitespace-pre-wrap">
        {segments.length > 0 ? segments : line}
      </p>
    );
  });
};

export const About: React.FC = () => {
  const [content, setContent] = useState(DEFAULT_ABOUT_TEXT);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await functionsService.getAboutContent();
        if (result?.success && result.content?.trim()) {
          setContent(result.content);
        }
      } catch (error) {
        console.error("Failed to load About content:", error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const rendered = useMemo(() => renderWithLinks(content), [content]);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="mb-6">
          <h1 className="h1 text-4xl mb-2">About Us</h1>
          <p className="body-text text-gray-600">
            Learn more about Blazerex and what we are building.
          </p>
        </div>

        <Card>
          <div className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blazerex-green"></div>
              </div>
            ) : (
              rendered
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
